import { useEffect, useState } from "react";
import type { MouseEvent, TouchEvent } from "react";
import { cx } from "class-variance-authority";
import { Loader } from "@googlemaps/js-api-loader";
import usePlacesAutocomplete from "use-places-autocomplete";
import { components } from "react-select";
import type { DropdownIndicatorProps, GroupBase, Props } from "react-select";

import { ChevronDown } from "../Icon/ChevronDown";
import { Pencil } from "../Icon/Pencil";
import { Select } from "./Select";

import * as styles from "./AddressSelect.module.scss";

const DropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => {
  const { selectProps, hasValue } = props;
  const extendedProps = selectProps as typeof selectProps & {
    onEdit?: () => void;
  };

  return (
    <components.DropdownIndicator {...props}>
      {hasValue ? (
        <Pencil
          className={styles.edit}
          onPointerDown={(e: MouseEvent | TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            extendedProps.onEdit?.();
          }}
        />
      ) : (
        <ChevronDown className={styles.open} />
      )}
    </components.DropdownIndicator>
  );
};

type AddressSelectProps = {
  label?: string;
  onChange?: (value: { value: string; label: string }) => void;
  placeholder?: string;
  country?: string;
  errorMessage?: string;
};

const AddressSelect = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  label,
  onChange,
  placeholder,
  country,
  errorMessage,
  onEdit,
  ...props
}: AddressSelectProps &
  Omit<Props<Option, IsMulti, Group>, "onChange"> & {
    onEdit?: () => void;
  }) => {
  const { init, setValue, suggestions, clearSuggestions } =
    usePlacesAutocomplete({
      initOnMount: false,
      requestOptions: country
        ? { componentRestrictions: { country } }
        : undefined,
      cacheKey: country ? `upa-${country}` : undefined,
    });

  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    const loadPlaces = async () => {
      const loader = new Loader({
        apiKey: "AIzaSyDz1LlLLClq7hLYOXWdrIjxGk0nSyu6b_c",
        version: "weekly",
        libraries: ["places"],
      });

      await loader.importLibrary("places");
      init();
    };

    loadPlaces();
  }, []);

  return (
    <Select
      {...props}
      placeholder={placeholder}
      label={label}
      inputValue={inputValue}
      onInputChange={(newValue, { action }) => {
        if (action === "input-change" || action === "set-value") {
          setInputValue(newValue);
          setValue(newValue);
        }
      }}
      options={
        suggestions.data.map((suggestion) => ({
          value: suggestion.place_id,
          label: suggestion.description,
        })) as any
      }
      onChange={(value) => onChange?.(value as any)}
      isLoading={suggestions.loading}
      filterOption={() => true}
      onBlur={() => clearSuggestions()}
      components={{ DropdownIndicator }}
      classNames={{
        control: () => cx({ [styles.hasError]: !!errorMessage }),
        singleValue: () => styles.inputValue,
        dropdownIndicator: () => styles.indicatorWrapper,
        menu: () => styles.menu,
        valueContainer: (state) =>
          cx(styles.valueContainer, { [styles.isFlex]: !state.hasValue }),
        input: () => styles.inputWrapper,
        placeholder: () => styles.placeholder,
      }}
      onEdit={onEdit}
    />
  );
};

export { AddressSelect };
export type { AddressSelectProps };
